// ?modal=expense-category&category=<id> on Setup's Agency tab (finance-real rounds 2 and 8, DD-045, DD-051): one expense
// category in one drawer, opened from Setup's Expense categories panel: rename it, make it a pot or a plain one again
// (J41), or delete it while no template or bill names it (TD-099), confirmed in the footer, never in a second drawer. The
// name and the pot save together (slice 12 R2). With no list behind it there is no Back, and a save or a delete closes it
// (J26, slice 10 R15). Given `onBack`, they hand back to that list. A category in use says so and offers no delete, and a
// pot something comes out of stays one. Bills point at the category, so a rename shows on all of them. The drawer is the
// catalogs' own (CatalogRowForm).
import { useState } from "react";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { CatalogRowForm } from "../components/CatalogRowForm";
import { ModalPending } from "../components/ModalPending";
import { useDeleteCategory, useExpenseTemplates, useUpdateCategory } from "../hooks/useExpenses";
import { StaticNotice } from "../jobs/JobParts";
import type { ExpenseCategory } from "../types";
import { EXPENSE_LISTS_FAILED } from "./ExpenseFields";
import { PotCheckbox } from "./PotCheckbox";

const CATEGORY = ENTITY_CONFIG.expenseCategory.label;
const expenseWord = ENTITY_CONFIG.expense.label.toLowerCase();

interface Props {
  categoryId: string;
  /** Back to a categories list, replacing this drawer. Setup's panel passes none (R15). */
  onBack?: () => void;
  onClose: () => void;
}

export function ExpenseCategoryModal({ categoryId, onBack, onClose }: Props) {
  // The templates' answer carries the categories, each with whether anything names it.
  const templates = useExpenseTemplates();
  const remove = useDeleteCategory();
  if (!templates.data) {
    return (
      <ModalPending
        title={CATEGORY}
        failure={templates.isError ? EXPENSE_LISTS_FAILED : null}
        retrying={templates.isFetching}
        retry={() => void templates.refetch()}
        onClose={onClose}
      />
    );
  }
  const category = templates.data.categories.find((c) => c.id === categoryId);
  if (!category) {
    // X and Escape close it, as every close of this one does (DD-045).
    return (
      <StaticNotice
        title={`${CATEGORY} not found`}
        message={`This category no longer exists. The current ones are in ${ENTITY_CONFIG.expense.label} ${ENTITY_CONFIG.expenseCategory.plural.toLowerCase()}.`}
        onClose={onClose}
      />
    );
  }
  return <CategoryForm key={category.id} category={category} categories={templates.data.categories} remove={remove} onBack={onBack} onClose={onClose} />;
}

interface FormProps {
  category: ExpenseCategory;
  categories: ExpenseCategory[];
  remove: ReturnType<typeof useDeleteCategory>;
  onBack?: () => void;
  onClose: () => void;
}

/** The catalogs' row form with the pot under the name: a changed box makes Save live and goes with the name. */
function CategoryForm({ category, categories, remove, onBack, onClose }: FormProps) {
  const update = useUpdateCategory();
  const [pot, setPot] = useState(category.pot);
  return (
    <CatalogRowForm
      row={category}
      rows={categories}
      noun={CATEGORY}
      inUseNote={`In use by an ${expenseWord} or a bill, so it stays. A new name shows on every one.`}
      deleteQuestion={`Delete ${category.label}? No ${expenseWord} or bill uses it.`}
      rename={{ send: (label) => update.mutateAsync({ id: category.id, label, pot }), pending: update.isPending }}
      remove={{ send: () => remove.mutateAsync(category.id), pending: remove.isPending }}
      onBack={onBack}
      onClose={onClose}
      dirty={pot !== category.pot}
    >
      <PotCheckbox checked={pot} onChange={setPot} locked={category.potInUse} disabled={update.isPending} />
    </CatalogRowForm>
  );
}
